import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { WEB_SOCKET_URL } from '../../assets/constants'
import { WebSocketResponseMessage, WebSocketResponseMessage_CandleSubscriptionResponse, WebSocketResponseMessage_OrderUpdate } from '../../proto/generated/websocket'
import { useSelector } from 'react-redux'
import { selectIsEmbedded } from '../../selectors/device'
import { isEmbeddedStrategyChartContext } from '../../util/embeddedChartContext'
import { isInitialDataFrame } from '../../services/websocket/initialData'
import { registerSocketHttpTransport } from '../../services/websocket/socketHttp'

/**
 * Frame listener. Every frame is decoded exactly once by the provider and the decoded message is
 * shared with all listeners; decoding per listener (a dozen of them at market open) was the main
 * source of allocation churn. Listeners must treat `message` as read-only.
 */
export type WebSocketFrameListener = (data: Uint8Array, message: WebSocketResponseMessage) => void

export interface WebSocketContextValue {
    socket: WebSocket | null
    sendMessage: (message: Uint8Array) => void
    addListener: (listener: WebSocketFrameListener) => void
    removeListener: (listener: WebSocketFrameListener) => void
    addOpenListener: (listener: () => void) => void
    removeOpenListener: (listener: () => void) => void
}

interface WebSocketProviderProps {
    children: ReactNode
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export { WebSocketContext }

const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
    const socketRef = useRef<WebSocket | null>(null)
    const [socket, setSocket] = useState<WebSocket | null>(null)
    const listeners = useRef<WebSocketFrameListener[]>([])
    const openListeners = useRef<(() => void)[]>([])
    const messageQueue = useRef<Uint8Array[]>([])
    const receivedInitialMessageRef = useRef(false)
    // Liveness: when the machine sleeps or the tab is frozen, the TCP connection can die without
    // the browser ever firing close (readyState stays OPEN, or close() hangs in CLOSING), so no
    // frame arrives again and the charts silently stop. Track the last frame and the last timer
    // tick; after a wake-up, visibility change or network change with no frame shortly after,
    // abandon the socket and open a new one without waiting for its close event.
    const lastFrameAtRef = useRef(0)
    const lastTickAtRef = useRef(Date.now())
    const livenessCheckRef = useRef<number | null>(null)
    const isEmbeded = useSelector(selectIsEmbedded)

    const notifyOpenListeners = useCallback(() => {
        openListeners.current.forEach((listener) => listener())
    }, [])

    const flushMessageQueue = useCallback((currentSocket: WebSocket) => {
        while (messageQueue.current.length > 0) {
            const message = messageQueue.current.shift()
            if (message) {
                currentSocket.send(message)
            }
        }
    }, [])

    const init = useCallback((): WebSocket => {
        const stale = socketRef.current
        if (stale && (stale.readyState === WebSocket.CLOSING || stale.readyState === WebSocket.CLOSED)) {
            // A close that never completed (dead peer) must not block a fresh connection.
            stale.onmessage = null
            stale.onclose = null
            stale.onerror = null
            socketRef.current = null
        }
        if (!socketRef.current) {
            const newSocket = new WebSocket(WEB_SOCKET_URL)
            newSocket.binaryType = 'arraybuffer'
            ;(window as any).getWebSocket = newSocket

            newSocket.onmessage = (msg) => {
                lastFrameAtRef.current = Date.now()
                const data = new Uint8Array(msg.data as ArrayBuffer)
                let message: WebSocketResponseMessage
                try {
                    message = WebSocketResponseMessage.decode(data)
                } catch (e) {
                    console.warn('Dropping undecodable websocket frame', e)
                    return
                }
                if (!receivedInitialMessageRef.current) {
                    // Only the frame carrying the bootstrap payloads counts; request/response replies
                    // and subscription snapshots can arrive before it, and a close after those must
                    // still trigger the HTTP fallback for the initial data.
                    receivedInitialMessageRef.current = isInitialDataFrame(message)
                }
                listeners.current.forEach((callback) => callback(data, message))
            }

            newSocket.onopen = () => {
                flushMessageQueue(newSocket)
                notifyOpenListeners()
            }

            newSocket.onclose = (ev) => {
                console.log('socket closed', ev, JSON.stringify(ev))
                if (!receivedInitialMessageRef.current) {
                    window.dispatchEvent(new Event('tick2trade:websocketInitialFailed'))
                }
                messageQueue.current = []
                socketRef.current = null
                setSocket(null)
                setTimeout(init, 2000)
            }

            newSocket.onerror = () => {
                newSocket.close()
            }

            socketRef.current = newSocket
            setSocket(newSocket)
        }

        return socketRef.current as WebSocket
    }, [flushMessageQueue, notifyOpenListeners])

    /** Drop the current socket (whatever state it claims) and connect again; subscriptions are re-sent on open. */
    const reconnect = useCallback((reason: string) => {
        const current = socketRef.current
        if (current) {
            console.log(`socket reconnect (${reason})`)
            current.onmessage = null
            current.onclose = null
            current.onerror = null
            try {
                current.close()
            } catch {
                /* already closed */
            }
            socketRef.current = null
            setSocket(null)
        }
        init()
    }, [init])

    // Reconnect when the connection has gone silent right after a wake-up / visibility / network change.
    const scheduleLivenessCheck = useCallback((reason: string) => {
        if (isEmbeded) return
        if (livenessCheckRef.current !== null) window.clearTimeout(livenessCheckRef.current)
        const since = Date.now()
        livenessCheckRef.current = window.setTimeout(() => {
            livenessCheckRef.current = null
            const current = socketRef.current
            if (!current) {
                init()
                return
            }
            if (lastFrameAtRef.current >= since && current.readyState === WebSocket.OPEN) return
            reconnect(reason)
        }, 5000)
    }, [init, isEmbeded, reconnect])

    useEffect(() => {
        if (isEmbeded) return
        const onVisibility = () => {
            if (document.visibilityState === 'visible') scheduleLivenessCheck('tab visible')
        }
        const onOnline = () => scheduleLivenessCheck('network online')
        const onPageShow = () => scheduleLivenessCheck('page shown')
        // A 1 s timer that misses by more than 10 s means the page was suspended (sleep, freeze).
        const watchdog = window.setInterval(() => {
            const now = Date.now()
            const gap = now - lastTickAtRef.current
            lastTickAtRef.current = now
            if (gap > 10_000) scheduleLivenessCheck(`resumed after ${Math.round(gap / 1000)}s`)
        }, 1000)
        document.addEventListener('visibilitychange', onVisibility)
        window.addEventListener('online', onOnline)
        window.addEventListener('pageshow', onPageShow)
        return () => {
            window.clearInterval(watchdog)
            document.removeEventListener('visibilitychange', onVisibility)
            window.removeEventListener('online', onOnline)
            window.removeEventListener('pageshow', onPageShow)
            if (livenessCheckRef.current !== null) window.clearTimeout(livenessCheckRef.current)
        }
    }, [isEmbeded, scheduleLivenessCheck])

    const sendMessage = useCallback(
        (message: Uint8Array) => {
            if (isEmbeded) return

            const currentSocket = init()

            if (currentSocket.readyState === WebSocket.OPEN) {
                currentSocket.send(message)
            } else {
                messageQueue.current.push(message)
            }
        },
        [isEmbeded, init]
    )

    useEffect(() => {
        if (!isEmbeded) return
        const chartBridge = isEmbeddedStrategyChartContext()
            ? window.strategyChartManager
            : window.chartManager
        if (!chartBridge) return
        chartBridge._publishToWebsocketManager = (candle: WebSocketResponseMessage_CandleSubscriptionResponse) => {
            const socketMessage = WebSocketResponseMessage.create()
            socketMessage.candleSubscriptionResponses = [candle]
            const m = WebSocketResponseMessage.encode(socketMessage).finish()

            if (listeners.current.length) {
                listeners.current.forEach((callback) => callback(m, socketMessage))
            }
        }
        if (isEmbeded && window.tradeManager) {
            window.tradeManager._publishOrderUpdatesToWebsocketManager = (orderUpdate: WebSocketResponseMessage_OrderUpdate) => {
                const socketMessage = WebSocketResponseMessage.create()
                socketMessage.orderUpdate = orderUpdate
                const m = WebSocketResponseMessage.encode(socketMessage).finish()

                if (listeners.current.length) {
                    listeners.current.forEach((callback) => callback(m, socketMessage))
                }
            }
        }
    }, [isEmbeded])

    useEffect(() => {
        if (!isEmbeded) {
            init()
        }

        return () => {
            if (socketRef.current) {
                listeners.current = []
                openListeners.current = []
                messageQueue.current = []
                socketRef.current.close()
                socketRef.current = null
            }
        }
    }, [isEmbeded, init])

    const addListener = useCallback((listener: WebSocketFrameListener) => {
        listeners.current.push(listener)
    }, [])

    const removeListener = useCallback((listener: WebSocketFrameListener) => {
        listeners.current = listeners.current.filter((ls) => ls !== listener)
    }, [])

    const addOpenListener = useCallback((listener: () => void) => {
        openListeners.current.push(listener)
    }, [])

    const removeOpenListener = useCallback((listener: () => void) => {
        openListeners.current = openListeners.current.filter((ls) => ls !== listener)
    }, [])

    // Expose the socket to non-React code for request/response calls (see services/websocket/socketHttp).
    // Re-registering on every socket change rejects requests left in flight on a closed socket.
    useEffect(() => {
        if (isEmbeded) {
            registerSocketHttpTransport(null)
            return
        }
        registerSocketHttpTransport({
            sendMessage,
            addListener,
            removeListener,
            getSocket: () => socketRef.current,
        })
        return () => registerSocketHttpTransport(null)
    }, [socket, isEmbeded, sendMessage, addListener, removeListener])

    const contextValue = useMemo<WebSocketContextValue>(
        () => ({
            socket,
            sendMessage,
            addListener,
            removeListener,
            addOpenListener,
            removeOpenListener,
        }),
        [socket, sendMessage, addListener, removeListener, addOpenListener, removeOpenListener]
    )

    return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>
}

export default WebSocketProvider
